import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference, uploadFileToS3 } from '@/helpers';
import { PageHeader } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import { Routes } from '@/router/routes';

import { useModelDetails } from '../api';
import { ModelForm } from '../components';
import { UpdateModelBody } from '../types';
import { ModelFormValues } from '../validations';

export const ModelDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();

  const { details, isFetching, uploadUrlDataMutation, updateModelMutation } =
    useModelDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      name: details.name,
      colorId: details.color?.id,
      typeId: details.type?.id,
      genderId: details.gender?.id,
      fileNameId: details.fileName?.id,
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
      status: details.status,
      archiveUrl: details.fileObject?.readUrl ?? '',
    };
  }, [details]);

  const updateModel = async (data: ModelFormValues) => {
    try {
      const { archiveUrl: _archiveUrl, ...body } = deepDifference<
        UpdateModelBody & { archiveUrl?: string | File }
      >(data, defaultValues);

      let key: string | null | undefined;

      if (data.archiveUrl instanceof File) {
        const { url, key: uploadKey } =
          await uploadUrlDataMutation.mutateAsync();
        await uploadFileToS3({ file: data.archiveUrl, uploadUrl: url });
        key = uploadKey;
      } else if (!data.archiveUrl && defaultValues?.archiveUrl) {
        key = null;
      }

      await updateModelMutation.mutateAsync({
        ...body,
        id: params.id,
        ...(key !== undefined && { key }),
      });
      toast.success(`Model “${data.name}” updated successfully!`);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <LoadingContainer loading={isFetching}>
      <ModelForm
        editMode
        onSubmit={updateModel}
        defaultValues={defaultValues}
        fileObject={details?.fileObject}
      >
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Models}
          actions={[
            <Button
              key="save-model"
              type="submit"
              data-testid="save-model-button"
              isLoading={
                updateModelMutation.isPending || uploadUrlDataMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      </ModelForm>
    </LoadingContainer>
  );
};
